<template>
  <div id="create-study-set-overview">
    <div class="base-create">
      <div class="create-class-overview">
        <el-row class="mb-px-16">
          <el-col :span="14">
            <div class="info-row">
              <p>
                <span class="label">{{ $t("read_aloud.review.read_aloud_name") }}:</span>
                <span class="value">{{ newReadAloud.name }}</span>
              </p>
            </div>
          </el-col>
          <el-col :span="10">
            <div class="info-row">
              <p>
                <span class="label">{{ $t("study_sets.overview.shareable") }}:</span>
                <span class="value">{{
                  $t($i18nLabelsHelper(currentOrganizationType, `study_sets.${newReadAloud.shareable}`))
                }}</span>
              </p>
            </div>
          </el-col>
        </el-row>
        <el-row class="mb-px-16">
          <el-col :span="10">
            <div class="info-row">
              <p>
                <span class="label">{{ $t("read_aloud.form.word_length") }}:</span>
                <span class="value">{{ newReadAloud.text.split(" ").length + " " + $t("read_aloud.form.words") }}</span>
              </p>
            </div>
          </el-col>
        </el-row>
        <el-row class="mb-px-16">
          <el-col :span="24">
            <div
              id="phrases-wrapper"
              v-bind:class="{
                'error-pharses':
                  studysetDetails && studysetDetails.phrases && studysetDetails.phrases.some(x => x.error),
              }"
            >
              <div class="phrases-wrapper__text">
                <div class="phrases-content-preview">{{ newReadAloud.text }}</div>
                <button-preview-audio v-if="audioReadAloudLink" :audio-link="audioReadAloudLink" :speed="audioSpeed" />
              </div>
            </div>
          </el-col>
        </el-row>
        <el-row class="mb-px-16">
          <el-col :span="24">
            <div class="info-row mb-px-16">
              <p>
                <span class="label">{{ $t("read_aloud.warm_up.title") }}</span>
              </p>
            </div>
            <div
              id="phrases-wrapper"
              v-bind:class="{
                'error-pharses':
                  studysetDetails && studysetDetails.phrases && studysetDetails.phrases.some(x => x.error),
              }"
            >
              <ul class="list-none" id="phrases-list">
                <li
                  v-for="phrase in studysetDetails.phrases"
                  :key="phrase.text"
                  :class="{ 'phrase-item--auto': phrase.isAutoGenerated }"
                >
                  <div class="phrase" :class="{ 'phrase--auto': phrase.isAutoGenerated }">
                    <el-tooltip
                      :content="$t('study_sets.overview.phrases_add_success')"
                      placement="top-start"
                      v-if="phrase.success"
                      ><i class="elsa-icon-check-circle"
                    /></el-tooltip>
                    <el-tooltip
                      :content="$t('study_sets.overview.phrases_add_changed', { original_text: phrase.original_text })"
                      placement="top-start"
                      v-else-if="phrase.warning"
                      ><i class="el-icon-warning"
                    /></el-tooltip>
                    <el-tooltip
                      :content="$t('study_sets.overview.phrases_add_fail')"
                      placement="top-start"
                      v-else-if="phrase.error"
                      ><i class="elsa-icon-exclamation-circle"
                    /></el-tooltip>
                    <i v-else style="color: transparent" />
                    <span
                      v-bind:class="{
                        'spell-error': phrase.classes && phrase.spell_error,
                        'cleanup-text': phrase.is_cleanup,
                      }"
                      >{{ phrase.text }}</span
                    >
                  </div>
                </li>
              </ul>
            </div>
          </el-col>
        </el-row>

        <el-row v-if="imageUpload && imageUpload.url" class="mb-px-16">
          <el-col :span="12">
            <div class="wrap-overview-image">
              <img :style="{ width: '100%', height: 'auto' }" :src="getUrlImage(imageUpload)" alt="" />
            </div>
          </el-col>
        </el-row>
      </div>
    </div>
  </div>
</template>

<script>
import { pathOr, isNil } from "ramda";
import { mapGetters, mapState } from "vuex";
import { DefaultShareableOptions, DefaultCategoryOptions } from "@/constants/studySets";
import { getOptionsLabels } from "@/utils/elementUi";
import ButtonPreviewAudio from "@/components/common/GenerateAudioTTS/ButtonPreviewAudio.vue";

export default {
  components: { ButtonPreviewAudio },
  props: {
    studysetDetails: {},
    organizationType: {},
    editClass: {
      type: Function,
      default: () => {},
    },
    showEditClass: {
      type: Boolean,
      default: true,
    },
    showUserInfo: {
      type: Boolean,
      default: true,
    },
    organizationId: {},
    uploadId: {
      default: null,
    },
    success: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      error: "",
      shareableOptions: DefaultShareableOptions,
      isPlayingAudio: false,
    };
  },
  computed: {
    ...mapState("readAlouds", ["newReadAloud", "imageUpload", "audioReadAloudLink", "audioSpeed"]),
    startEndDate() {
      const not_selected = this.$t("classes.create_class_flow.class_overview.not_selected");
      const start_date = isNil(this.studysetDetails.startDate)
        ? not_selected
        : new Date(this.studysetDetails.startDate).toDateString();
      const end_date = isNil(this.studysetDetails.endDate)
        ? not_selected
        : new Date(this.studysetDetails.endDate).toDateString();

      return `${start_date} - ${end_date}`;
    },
    classId() {
      return pathOr(null, ["studysetDetails", "id"])(this);
    },
    ...mapGetters({
      currentUser: "user/getUser",
      currentOrganizationType: "organization/currentOrganizationType",
    }),
    phrases_count() {
      if (this.studysetDetails && this.studysetDetails.phrases) {
        return this.studysetDetails.phrases.length;
      }
      return 0;
    },
  },
  methods: {
    getShareableLabel(v) {
      return getOptionsLabels(DefaultShareableOptions, v);
    },
    getCategoryLabel(v) {
      return getOptionsLabels(DefaultCategoryOptions, v);
    },
    getUrlImage(file) {
      if (!file || !file.raw) return "";
      return URL.createObjectURL(file.raw);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../scss/_colors.scss";

h3 {
  text-transform: none;
}

.class-teacher {
  margin-top: 40px;

  .user-info {
    display: flex;
    flex-direction: column;

    .name {
      color: $black;
    }
  }
}

p {
  font-weight: 300;
  font-size: 18px;
  color: $dark-blue-2;
  margin: 0;
}

.practice {
  display: flex;
  align-items: center;
  margin-top: 24px;

  .input-wrapper {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
    color: $black;

    .goal-value {
      font-weight: bold;
      font-size: 20px;
      color: #478dff;
      padding: 0 5px;
    }
  }

  .description {
    p {
      margin-top: 5px;
      font-size: 16px;
      line-height: 24px;
      opacity: 0.7;
    }

    .assessment-test-title {
      font-weight: 500;
      font-size: 20px;
      line-height: 24px;
      color: $black;
      opacity: 1;
    }
  }

  .box-card {
    border: 1px solid #e6e6e6;
    box-sizing: border-box;
    border-radius: 16px;
    // margin-bottom: 24px;
    height: 214px;
    width: 100%;

    ::v-deep .el-card__body {
      align-items: flex-start;

      .card-number {
        margin-bottom: 8px;
      }

      .card-content {
        flex: 1 0;

        h2 {
          font-size: 24px;
          line-height: 36px;
          color: #121131;
        }

        p {
          font-size: 16px;
          line-height: 24px;
          color: #121131;
        }

        button {
          float: right;
        }
      }
    }
  }
}

.line-separate {
  border: 1px solid#E6E6E6;
  margin: 40px 0 32px;
}

#create-study-set-overview {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 24px 32px;
}

.info-row {
  p {
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: #757575;
    margin: 0;
    text-align: initial;
  }

  .value {
    color: $black;
    text-transform: none;
    margin-bottom: 4px;
  }
}

.error-row {
  p {
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    color: red;
    margin: 0;
    text-align: initial;
  }
}

#phrases-wrapper {
  background: #edf4ff;
  border-radius: 8px;
  padding: 16px;
  min-height: 200px;
  max-height: 300px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: #edf4ff;
    border-radius: 100px;
  }

  &::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 8px #c2c2c2;
    border-radius: 100px;
  }
  .phrases-wrapper__text {
    display: flex;
    gap: 6px;
    .phrases-content-preview {
      flex: 1;
    }
  }
}

#phrases-list {
  overflow: auto;
  padding-left: 0px;
  margin-top: 12px;
  margin-bottom: 12px;
  display: flex;
  padding: 0;
  gap: 10px;
  flex-wrap: wrap;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: #edf4ff;
    border-radius: 100px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #c2c2c2;
    // box-shadow: inset 0 0 8px #C2C2C2;
    border-radius: 100px;
  }

  .spell-error {
    text-decoration-line: underline;
    color: #eb001b;
  }

  .cleanup-text {
    text-decoration-line: underline;
    color: #ff9f00;
  }
}

#phrases-list li {
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */
  color: #121131;
  display: flex;

  .phrase {
    display: flex;
    border: 0.5px solid #cee0ea;
    background: #e9e9ec;
    padding: 6px 10px;
    border-radius: 4px;

    .el-icon-warning {
      font-size: 18px;
      color: #ff9f00;
      padding: 4px 6px 0px 0px;
    }
  }
}

.error-pharses {
  border: 1px solid red;
}

.phrase {
  font-size: 14px;
  font-weight: 590;

  &--auto {
    background: #b6d1ff !important;
  }
}

.wrap-overview-image {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  height: 264px;
  width: 396px;
  border: 1px solid #e6e6e6;
  border-radius: 8px;

  img {
    object-fit: contain;
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
  }
}
</style>
